// action/saleReturnActions.js
import { salesReturnsAPI } from './api';
import { salesAPI } from './api';
import { customerAPI } from './api';
//import { inventoryAPI } from '../api/inventory';

export const saleReturnActions = {
    // Get customers
    getCustomers: async (params = {}) => {
        try {
            const response = await customerAPI.getAll(params);
            if (response.status && response.data) {
                return { success: true, data: response.data };
            }
            return { success: false, message: 'No customers found', data: [] };
        } catch (error) {
            console.error('Error fetching customers:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch customers',
                data: []
            };
        }
    },

    // Search sales by invoice number
   searchSalesByInvoice: async (searchTerm) => {
    try {
        if (!searchTerm || searchTerm.length < 3) {
            return { success: false, message: 'Please enter at least 3 characters', data: [] };
        }
        
        console.log('Searching for invoice:', searchTerm);
        
        // Call the dedicated search API
        const response = await salesReturnsAPI.searchSalesByInvoice({ 
            search: searchTerm,
            limit: 10
        });
        
        console.log('Search response:', response);
        
        if (response && response.success) {
            if (response.data && response.data.length > 0) {
                // Format the sales data for dropdown
                const sales = response.data.map(sale => ({
                    id: sale.id,
                    invoice_no: sale.invoice_no,
                    sale_date: sale.sale_date,
                    customer_id: sale.customer_id,
                    customer_name: sale.customer_name,
                    customer_phone: sale.customer_phone,
                    grand_total: sale.grand_total,
                    paid_amount: sale.paid_amount,
                    due_amount: sale.due_amount,
                    status: sale.status,
                    payment_status: sale.payment_status
                }));
                
                return {
                    success: true,
                    data: sales,
                    message: `${sales.length} sale(s) found`
                };
            } else {
                return {
                    success: false,
                    message: 'No sales found with this invoice number',
                    data: []
                };
            }
        } else {
            return {
                success: false,
                message: response?.message || 'Failed to search sales',
                data: []
            };
        }
    } catch (error) {
        console.error('Error searching sales:', error);
        return {
            success: false,
            message: error.response?.data?.message || error.message || 'Failed to search sales',
            data: []
        };
    }
},

    // Get original sale details for return
    getSaleForReturn: async (saleId) => {
        try {
            const response = await salesAPI.get(saleId);
            
            if (response && response.data) {
                const sale = response.data.sale || response.data;
                const items = response.data.items || [];
                
                // Also check for any existing returns for this sale
                // This would be an API call to get already returned quantities
                // For now, we'll assume no returns
                
                return {
                    success: true,
                    data: {
                        sale: {
                            id: sale.id,
                            invoice_no: sale.invoice_no,
                            sale_date: sale.sale_date,
                            customer_id: sale.customer_id,
                            customer_name: sale.customer_name,
                            customer_phone: sale.customer_phone,
                            grand_total: sale.grand_total
                        },
                        items: items.map(item => ({
                            id: item.id,
                            product_id: item.product_id,
                            product_code: item.product_code || item.sku,
                            product_name: item.product_name,
                            sku: item.sku,
                            quantity: item.quantity,
                            original_quantity: item.quantity,
                            unit_price: item.unit_price,
                            discount: item.discount || 0,
                            tax_percent: item.tax_percent || 0,
                            tax_amount: item.tax_amount || 0,
                            total_cost: item.total_cost,
                            max_return_qty: item.quantity, // Maximum quantity that can be returned
                            return_quantity: 0,
                            selected: false
                        }))
                    }
                };
            }
            return { success: false, message: 'Sale not found' };
        } catch (error) {
            console.error('Error fetching sale:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch sale' 
            };
        }
    },

    // Create sale return

// In saleReturnActions.js

createReturn: async (returnData) => {
    try {
        console.log('createReturn received:', returnData);
        
        // Validate required fields
        if (!returnData.sale_id) {
            return { success: false, message: 'Original sale is required' };
        }
        
        if (!returnData.customer_id) {
            return { success: false, message: 'Customer is required' };
        }
        
        let items = returnData.items;
        if (!items || items.length === 0) {
            return { success: false, message: 'At least one item is required for return' };
        }
        
        // Filter only items with return quantity > 0
        const returnItems = items.filter(item => item.quantity > 0);
        
        if (returnItems.length === 0) {
            return { success: false, message: 'No items selected for return' };
        }
        
        // Validate each return item
        for (const item of returnItems) {
            if (item.quantity > item.max_return_qty) {
                return { 
                    success: false, 
                    message: `Return quantity for ${item.product_name} cannot exceed ${item.max_return_qty}` 
                };
            }
        }
        
        // Calculate totals from items
        const subtotal = returnItems.reduce((sum, item) => 
            sum + (item.unit_price * item.quantity), 0);
        
        const taxAmount = returnItems.reduce((sum, item) => 
            sum + (item.tax_amount || 0), 0);
        
        const grandTotal = subtotal + taxAmount;
        
        // Generate return reference if not provided
        const reference_no = returnData.reference_no || `SRN${Date.now().toString().slice(-8)}`;
        
        const dataToSend = {
            sale_id: returnData.sale_id,
            reference_no: reference_no,
            original_invoice_no: returnData.original_invoice_no,
            customer_id: returnData.customer_id,
            return_date: returnData.return_date || new Date().toISOString().split('T')[0],
            status: returnData.status || 'pending',
            refund_method: returnData.refund_method || '',
            reason: returnData.reason || '',
            notes: returnData.notes || '',
            paid_amount: returnData.paid_amount || 0,
            due_amount: returnData.due_amount || 0,
            payment_status: returnData.payment_status || 'unpaid',
            subtotal: subtotal,
            tax: taxAmount,
            grand_total: grandTotal,
            round_off: returnData.round_off || 0,
            enable_rounding: returnData.enable_rounding || false,
            refund_to_credit: returnData.refund_method === 'credit',
            items: returnItems.map(item => ({
                sale_item_id: item.sale_item_id || item.id,
                product_id: item.product_id,
                product_name: item.product_name,
                sku: item.sku,
                quantity: item.quantity,
                unit_price: item.unit_price,
                tax_percent: item.tax_percent || 0,
                tax_amount: item.tax_amount || 0,
                total_cost: item.total_cost || (item.unit_price * item.quantity + (item.tax_amount || 0)),
                reason: item.reason || returnData.reason || ''
            }))
        };
        
        console.log('Sending to API:', JSON.stringify(dataToSend, null, 2));
        
        const response = await salesReturnsAPI.create(dataToSend);
        console.log('API response:', response);
        
        if (response && response.success) {
            return { 
                success: true, 
                data: response.data,
                message: 'Return created successfully'
            };
        } else {
            return { 
                success: false, 
                message: response?.message || 'Failed to create return' 
            };
        }
    } catch (error) {
        console.error('Error creating return:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || error.message || 'Failed to create return' 
        };
    }
},
    // Get sale returns list
    getReturns: async (params = {}) => {
        try {
            const response = await salesReturnsAPI.list(params);
            
            if (response.data && response.data.returns) {
                return {
                    success: true,
                    data: response.data.returns,
                    total: response.data.total,
                    page: response.data.page,
                    limit: response.data.limit,
                    totalPages: response.data.total_pages,
                    summary: response.data.summary
                };
            } else if (response.returns) {
                return {
                    success: true,
                    data: response.returns,
                    total: response.total,
                    page: response.page,
                    limit: response.limit,
                    totalPages: response.total_pages
                };
            } else {
                return {
                    success: false,
                    message: 'Invalid API response format',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching returns:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch returns',
                data: []
            };
        }
    },

    // Get single sale return
    getReturn: async (id) => {
        try {
            const response = await salesReturnsAPI.get(id);
            
            if (!response) {
                return { success: false, message: 'Return not found' };
            }
            
            if (response.success && response.data) {
                return { success: true, data: response.data };
            } else if (response.data) {
                return { success: true, data: response.data };
            } else {
                return { success: true, data: response };
            }
        } catch (error) {
            console.error('Error fetching return:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to fetch return' 
            };
        }
    },

    // Update return status
    updateStatus: async (id, status) => {
        try {
            const response = await salesReturnsAPI.updateStatus(id, status);
            
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data,
                    message: `Status updated to ${status}`
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to update status' 
                };
            }
        } catch (error) {
            console.error('Error updating status:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update status' 
            };
        }
    },

   // In saleReturnActions.js, update the updateReturn method

updateReturn: async (id, updateData) => {
    try {
        if (!id) {
            return { success: false, message: 'Return ID is required' };
        }
        
        console.log('Updating return:', id, updateData);
        
        // Filter out any fields that might not exist in the database
        const cleanData = {
            id: id,
            return_date: updateData.return_date,
            status: updateData.status,
            payment_status: updateData.payment_status,
            refund_method: updateData.refund_method,
            reason: updateData.reason,
            notes: updateData.notes,
            paid_amount: updateData.paid_amount,
            due_amount: updateData.due_amount,
            grand_total: updateData.grand_total,
            items: updateData.items
        };
        
        // Only include rounding fields if they exist in your data
        // The backend will check if the columns exist
        if (updateData.rounded_grand_total !== undefined) {
            cleanData.rounded_grand_total = updateData.rounded_grand_total;
        }
        
        if (updateData.round_off !== undefined) {
            cleanData.round_off = updateData.round_off;
        }
        
        if (updateData.enable_rounding !== undefined) {
            cleanData.enable_rounding = updateData.enable_rounding ? 1 : 0;
        }
        
        console.log('Clean data being sent:', cleanData);
        
        const response = await salesReturnsAPI.update(id, cleanData);
        
        if (response && response.success) {
            return { 
                success: true, 
                data: response.data,
                message: 'Return updated successfully'
            };
        } else {
            return { 
                success: false, 
                message: response?.message || 'Failed to update return' 
            };
        }
    } catch (error) {
        console.error('Error updating return:', error);
        return { 
            success: false, 
            message: error.response?.data?.message || error.message || 'Failed to update return' 
        };
    }
},

    // Delete sale return
    deleteReturn: async (deleteData) => {
        try {
            if (!deleteData.id && !deleteData) {
                return { success: false, message: 'Return ID is required' };
            }
            
            const id = typeof deleteData === 'object' ? deleteData.id : deleteData;
            const data = { id };
            
            const response = await salesReturnsAPI.delete(data);
            
            if (response && (response.success || response.status || response.deleted)) {
                return { 
                    success: true, 
                    data: response,
                    message: response.message || 'Return deleted successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to delete return' 
                };
            }
        } catch (error) {
            console.error('Error deleting return:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to delete return' 
            };
        }
    },

    // Process refund
    processRefund: async (refundData) => {
        try {
            if (!refundData.return_id) {
                return { success: false, message: 'Return ID is required' };
            }
            
            if (!refundData.refund_method) {
                return { success: false, message: 'Refund method is required' };
            }
            
            const response = await salesReturnsAPI.processRefund(refundData);
            
            if (response && response.success) {
                return { 
                    success: true, 
                    data: response.data,
                    message: 'Refund processed successfully'
                };
            } else {
                return { 
                    success: false, 
                    message: response?.message || 'Failed to process refund' 
                };
            }
        } catch (error) {
            console.error('Error processing refund:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to process refund' 
            };
        }
    },

    // Get customer credits
    getCustomerCredits: async (customerId) => {
        try {
            const response = await salesReturnsAPI.getCustomerCredits(customerId);
            
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data
                };
            }
            return {
                success: false,
                message: response?.message || 'Failed to fetch customer credits',
                data: { credits: [], total: 0 }
            };
        } catch (error) {
            console.error('Error fetching customer credits:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch customer credits',
                data: { credits: [], total: 0 }
            };
        }
    }
};

export default saleReturnActions;